import { Component, inject, OnInit }                               from '@angular/core';
import { HttpErrorResponse }                                       from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router }                                                  from '@angular/router';
import { MatError, MatFormField, MatLabel }                        from '@angular/material/form-field';
import { MatInput }                                                from '@angular/material/input';
import { MatProgressSpinner }                                      from '@angular/material/progress-spinner';
import { MatIcon }                                                 from '@angular/material/icon';

import { RegistrationNotification }        from './registration-notification';
import { RegistrationData }                from '../../../core/auth/auth.service';
import { PasswordTooltipDirective }        from '../../../shared/directives/password-tooltip.directive';
import { PasswordUtils }                   from '../../../shared/utils/password.utils';
import { FormUtils }                       from '../../../shared/utils/form.utils';
import { StringUtils }                     from '../../../shared/utils/string.utils';
import { AccessibilityUtils }              from '../../../shared/utils/accessibility.utils';
import { asciiOnlyValidator }              from '../../../shared/validators/ascii-only.validator';
import { AuthenticationService }           from '../../../core/auth/authentication.service';
import { PinNotificationComponent }        from '../../../shared/components/notification/notification.component';
import { TooltipComponent }                from '../../../shared/components/tooltip/tooltip.component';
import { TrimFieldDirective }              from '../../../shared/directives/trim-field.directive';
import { PasswordInputComponent }          from '../../../shared/components/password-input/password-input.component';
import { PasswordInfoComponent }           from '../../../shared/components/dialog/password-info/password-info.component';
import { ButtonComponent }                 from '../../../shared/components/buttons/button.component';
import { InternalRoutes }                  from '../../../shared/data/internal-routes';
import { ErrorService }                    from '../../../core/error-handling/error.service';

import { interval, Observable }            from 'rxjs';
import { take }                            from 'rxjs/operators';

@Component({
  selector: 'pin-login-page',
  templateUrl: './registration.component.html',
  hostDirectives: [PasswordTooltipDirective],
  imports: [PinNotificationComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, TooltipComponent, TrimFieldDirective, PasswordInputComponent, PasswordInfoComponent, ButtonComponent, MatIcon, MatProgressSpinner]
})
export class RegistrationComponent implements OnInit {
  readonly passwordTooltip = inject(PasswordTooltipDirective);
  returnUrl: string;
  loading = false;
  resendingEmailLocked = false;
  isRegistrationPage = true;
  registrationNotification: RegistrationNotification;
  registrationNotificationValues = RegistrationNotification;
  public registrationForm = new FormGroup(
    {
      accountIdentifier: new FormControl<string>('', [Validators.required]),
      unifiedTaxIdLast4Digits: new FormControl<string>('', [Validators.required]),
      email: new FormControl<string>('', [Validators.required, Validators.email]),
      firstName: new FormControl<string>('', [Validators.required, asciiOnlyValidator()]),
      lastName: new FormControl<string>('', [Validators.required, asciiOnlyValidator()]),
      username: new FormControl<string>('', [Validators.required, asciiOnlyValidator(StringUtils.USERNAME_ILLEGAL_CHARACTERS)]),
      password: new FormControl<string>('', [Validators.required, Validators.pattern(StringUtils.PASSWORD_VALIDATION_REGEX)]),
      passwordConfirmation: new FormControl<string>('', [Validators.required])
    },
    [PasswordUtils.samePasswordValidator]
  );

  private authService = inject(AuthenticationService);
  private errorService = inject(ErrorService);
  private router = inject(Router);
  private username: string;

  private resendTimer: Observable<number> | null;
  public allowResendIn = 'now';

  ngOnInit() {
    this.isRegistrationPage = this.router.url === '/register';
    this.username = localStorage.getItem('registerUsername');
  }

  startResendTimer()
  {
    this.allowResendIn = 'in 1:00';

    const countdownSeconds = 60;
    this.resendTimer = interval(1000).pipe(
      take(countdownSeconds));
    this.resendTimer.subscribe({
      next: value => {
        this.allowResendIn = `in 0:${String(countdownSeconds - value - 1).padStart(2, '0')}`;
      },
      complete: () => {
        this.allowResendIn = 'now'
        this.resendingEmailLocked = false;
      }
    });
  }

  sendRegistration() {
    if (!this.registrationForm.valid) {
      FormUtils.markAllControlsTouchedAndValidate(this.registrationForm);
      return;
    }
    this.loading = true;
    const formDetails: RegistrationData = {
      ...this.registrationForm.getRawValue(),
      invitationId: null
    };

    this.authService
      .register(formDetails)
      .subscribe({
        next: () => {
          this.loading = false;
          localStorage.setItem('registerUsername', formDetails.username);
          this.registrationNotification = undefined;
          this.router.navigate([], {fragment: 'post-registration'})
            .then(() => {
              window.location.reload();
            });
        },
        error: error => {
          this.loading = false;
          this.displayError(error);
        }
      });
  }


  onPasswordBlur() {
    this.passwordTooltip.onPasswordBlur();
    this.registrationForm.controls['passwordConfirmation'].updateValueAndValidity();
  }

  navigateToLogin(event) {
    if (AccessibilityUtils.shouldTriggerLinkNavigation(event)) {
      this.router.navigate(['/login']).then();
    }
  }

  resendConfirmationEmail() {
    this.startResendTimer();

    if (this.resendingEmailLocked) {
      return;
    }

    this.resendingEmailLocked = true;
    this.clearNotification();
    this.router.navigate([], {fragment: 'post-registration'}).then();
    this.authService.resendConfirmationMail(this.username).subscribe({
      next: () => {
        this.displayNotification(this.registrationNotificationValues.resend_confirmation_mail_success);
      },
      error: (error) => {
        this.handleError(error);
      }
    });
  }

  handleError(error: HttpErrorResponse) {
    if (error?.error?.message === 'ERR_LIMIT_EXCEEDED') {
      this.displayNotification(this.registrationNotificationValues.limit_exceeded_exception);
    } else {
      throw error;
    }
  }

  clearNotification() {
    this.registrationNotification = undefined;
  }


  private displayError(error: HttpErrorResponse) {
    if (this.errorService.isInfrastructureError(error)) {
      this.displayNotification(RegistrationNotification.try_again_later);
      return;
    }

    switch (error?.error?.message) {
      case 'ERR_CUSTOMER_ID_REGISTERED':
        this.displayNotification(RegistrationNotification.customer_already_registered);
        break;
      case 'ERR_USERNAME_ALREADY_EXISTS':
        this.displayNotification(RegistrationNotification.username_already_exists);
        break;
      case 'ERR_EMAIL_ALREADY_EXISTS':
        this.displayNotification(RegistrationNotification.email_already_exists);
        break;
      case 'ERR_REQUEST_VALIDATION_EXCEPTION':
        this.displayNotification(RegistrationNotification.data_validation_error);
        break;
      case 'ERR_DCP_SELF_REGISTRATION_DISABLED':
        this.displayNotification(RegistrationNotification.self_registration_disabled);
        break;
      case 'RESEND_CONFIRMATION_MAIL_SUCCESS':
        this.displayNotification(RegistrationNotification.resend_confirmation_mail_success);
        break;
      case 'LIMIT_EXCEEDED_EXCEPTION':
        this.displayNotification(RegistrationNotification.limit_exceeded_exception);
        break;
      default:
        this.router.navigate([InternalRoutes.ERROR]).then();
    }
  }

  private displayNotification(notification: RegistrationNotification): void {
    this.registrationNotification = notification;

    window.scroll(0, 0);
  }
}
